import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RequestSection } from './Overview_subtabs/RequestSection';
import SuppliersSection from './Overview_subtabs/SuppliersSection';
import { QuotationsSection } from "./Overview_subtabs/QuotationsSection";
import { PurchaseOrderSection } from "./Overview_subtabs/PurchaseOrderSection";
import WorkflowSection from "./WorkflowSection";
import { getOrderItems, getPurchaseOrdersByIPRId, getIPRProducts } from "../../../../ApiCalls/mantisFlowsManangment";
import {
  getSuppliers,
  getSuppliersByOrderId,
} from "../../../../ApiCalls/mantisFlowsSuppliers";
import { useToast } from "@mantis-sas/ui";
import { ChevronDown, ChevronRight, Package, Users, FileText, ShoppingCart, Workflow } from "lucide-react";
import { loadQuotationItems } from "../Utils/utils";
import { motion, AnimatePresence } from "framer-motion";
import "./Overview_subtabs/DeliveryReconciliationSection";

// Definición de las secciones del overview (incluyendo workflow)
const OVERVIEW_SECTIONS = [
  {
    key: "PO Creation",
    title: "Gestión de Órdenes de Compra",
    tabName: "Órdenes de Compra",
    description: "Gestionar órdenes de compra",
    icon: ShoppingCart,
    component: PurchaseOrderSection,
    color: "orange",
  },
  {
    key: "Workflow",
    title: "Flujo de la Solicitud",
    tabName: "Flujo",
    description: "",
    icon: Workflow,
    component: WorkflowSection,
    color: "indigo",
    alwaysOpen: false,
  },
  {
    key: "Requests",
    title: "Artículos de la Solicitud",
    tabName: "Artículos",
    description: "Artículos de la Solicitud",
    icon: Package,
    component: RequestSection,
    color: "blue",
  },
  {
    key: "Suppliers",
    title: "Selección de Proveedores",
    tabName: "Proveedores",
    description: "Selección y gestión de proveedores",
    icon: Users,
    component: SuppliersSection,
    color: "green",
  },
  {
    key: "Quotations",
    title: "Cotizaciones",
    tabName: "Cotizaciones",
    description: "Solicitud de cotizaciones y respuestas de proveedores",
    icon: FileText,
    component: QuotationsSection,
    color: "purple",
  },
  /* {
    key: "Delivery & Reconciliation",
    title: "Entrega y Conciliación",
    tabName: "Entregas",
    description: "Seguimiento de entregas y conciliación de pedidos",
    icon: Truck,
    component: DeliveryReconciliationSection,
    color: "indigo",
  }, */
];

// Componente de navegación rápida
const QuickNavigation = ({ sections, onNavigate, expandedSections }) => {
  return (
    <div className="">
      <div className="flex flex-wrap gap-2">
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections[section.key];

          return (
            <button
              key={section.key}
              onClick={() => onNavigate(section.key)}
              className={`
                flex items-center space-x-2 px-3 py-2 rounded text-sm font-medium transition-all
                ${
                  isExpanded
                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700"
                    : "bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600"
                }
              `}
            >
              <Icon className="h-5 w-5" />
              <span>{section.tabName}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Componente para cada sección desplegable
const CollapsibleSection = ({
  section,
  isExpanded,
  onToggle,
  children,
  isLoading = false,
}) => {
  const { title, description, icon: Icon, color, alwaysOpen } = section;

  const colorClasses = {
    blue: "border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10",
    green:
      "border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/10",
    purple:
      "border-purple-200 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/10",
    orange:
      "border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10",
    indigo:
      "border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/10",
  };

  const iconColors = {
    blue: "text-blue-600 dark:text-blue-400",
    green: "text-green-600 dark:text-green-400",
    purple: "text-purple-600 dark:text-purple-400",
    orange: "text-orange-600 dark:text-orange-400",
    indigo: "text-indigo-600 dark:text-indigo-400",
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header clickeable */}
      <button
        onClick={alwaysOpen ? undefined : onToggle}
        disabled={alwaysOpen}
        className={`w-full px-6 py-4 flex items-center justify-between ${
          !alwaysOpen ? "hover:bg-slate-50 dark:hover:bg-slate-700/50" : ""
        } transition-colors border-l-4 ${colorClasses[color]} ${
          alwaysOpen ? "cursor-default" : "cursor-pointer"
        }`}
      >
        <div className="flex items-center space-x-4">
          <div
            className={`w-10 h-10 rounded ${colorClasses[color]} flex items-center justify-center`}
          >
            <Icon className={`h-5 w-5 ${iconColors[color]}`} />
          </div>
          <div className="text-left">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {title}
              </h3>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {description}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isLoading && (
            <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
          )}
          {!alwaysOpen &&
            (isExpanded ? (
              <ChevronDown className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronRight className="h-5 w-5 text-slate-400" />
            ))}
        </div>
      </button>

      {/* Contenido desplegable */}
      <AnimatePresence>
        {(isExpanded || alwaysOpen) && (
          <motion.div
            initial={alwaysOpen ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={alwaysOpen ? false : { height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-200 dark:border-slate-700">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function OverviewSection({
  data,
  currentUser,
  clientId,
  mantisBusiness,
  token,
}) {
  const { toast } = useToast();
  console.log("data123", data);

  // Log para verificar que los datos del usuario llegan correctamente al OverviewSection
  console.log("🔍 OverviewSection - Datos del usuario recibidos:", {
    currentUser: currentUser,
    currentUserId: currentUser?._id,
    currentUserEmail:
      currentUser?.email ||
      currentUser?.corporateEmail ||
      currentUser?.personalEmail,
    mantisBusiness: mantisBusiness,
    clientId: clientId,
  });

  // Estados para controlar qué secciones están expandidas
  const [expandedSections, setExpandedSections] = useState({
    Workflow: false, // Will be set based on purchase orders
    Suppliers: false,
    Quotations: false,
    "PO Creation": false, // Will be set based on purchase orders
    "Delivery & Reconciliation": false,
  });

  // Estados centralizados (mantener los mismos del código original)
  const [orderItems, setOrderItems] = useState([]);
  const [iprProducts, setIprProducts] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [orderSuppliers, setOrderSuppliers] = useState([]);
  const [allQuotationItems, setAllQuotationItems] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [selectedSuppliersIds, setSelectedSuppliersIds] = useState([]);
  useState(null);
  useState([]);
  useState(null);
  useState(false);
  useState(false);
  useState(false);
  useState("");
  useState({});
  const [, setSseConnection] = useState(null);
  const sseConnectionRef = useRef(null);
  const isLoadingOrderSuppliersRef = useRef(false);

  const [isLoading, setIsLoading] = useState({
    orderItems: false,
    suppliers: false,
    orderSuppliers: false,
    allQuotationItems: false,
    purchaseOrders: false,
    iprProducts: false,
  });
  const [errors, setErrors] = useState({
    orderItems: null,
    suppliers: null,
    orderSuppliers: null,
    purchaseOrders: null,
    iprProducts: null,
  });

  // Función para agregar logs
  const addLog = useCallback(async (message, type = "info", step = "null") => {
    try {
      console.log(`[${type.toUpperCase()}] ${step}: ${message}`);
    } catch (error) {
      console.error("Error adding log:", error);
    }
  }, []);

  // Función para alternar secciones
  const toggleSection = (sectionKey) => {
    const section = OVERVIEW_SECTIONS.find((s) => s.key === sectionKey);
    if (section?.alwaysOpen) return; // No permitir cerrar secciones siempre abiertas

    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // Función para navegación rápida
  const navigateToSection = (sectionKey) => {
    const section = OVERVIEW_SECTIONS.find((s) => s.key === sectionKey);
    if (section?.alwaysOpen) {
      // Scroll a la sección si siempre está abierta
      const element = document.getElementById(`section-${sectionKey}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    // Expandir la sección si está cerrada
    if (!expandedSections[sectionKey]) {
      setExpandedSections((prev) => ({
        ...prev,
        [sectionKey]: true,
      }));
    }

    // Scroll a la sección después de un pequeño delay para la animación
    setTimeout(() => {
      const element = document.getElementById(`section-${sectionKey}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 300);
  };

  // Cargar Order Items (mantener lógica original)
  const loadOrderItems = useCallback(async () => {
    if (!clientId || !data?.id) return;

    try {
      setIsLoading((prev) => ({ ...prev, orderItems: true }));
      setErrors((prev) => ({ ...prev, orderItems: null }));

      const items = await getOrderItems(clientId, data.id);
      setOrderItems(items.data);
    } catch (error) {
      console.error("Error loading order items:", error);
      setErrors((prev) => ({
        ...prev,
        orderItems: "Failed to load order items. Please try again.",
      }));
      toast({
        title: "Error",
        description: "Failed to load order items",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, orderItems: false }));
    }
  }, [clientId, data?.id, toast]);

  // Cargar Order Items (mantener lógica original)
  const loadIprProducts = useCallback(async () => {
    if (!clientId || !data?.id) return;

    try {
      setIsLoading((prev) => ({ ...prev, iprProducts: true }));
      setErrors((prev) => ({ ...prev, iprProducts: null }));

      const products = await getIPRProducts(clientId, data.id);
      console.log("iprProducts", products);
      setIprProducts(products.data);
    } catch (error) {
      console.error("Error cargando artículos del pedido:", error);
      setErrors((prev) => ({
        ...prev,
        orderItems:
          "Error al cargar los artículos del pedido. Por favor intenta de nuevo.",
      }));
      toast({
        title: "Error",
        description: "Error al cargar los artículos del pedido",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, iprProducts: false }));
    }
  }, [clientId, data?.id, toast]);

  // Cargar Order Items (mantener lógica original)
  const loadPurchaseOrders = useCallback(async () => {
    if (!clientId || !data?.id) return;

    try {
      setIsLoading((prev) => ({ ...prev, purchaseOrders: true }));
      setErrors((prev) => ({ ...prev, purchaseOrders: null }));

      const orders = await getPurchaseOrdersByIPRId(clientId, {}, data.id);
      setPurchaseOrders(orders.data);

      // Auto-expand PO Creation section if there are existing purchase orders
      if (orders.data && orders.data.length > 0) {
        setExpandedSections((prev) => ({
          ...prev,
          "PO Creation": true,
          Workflow: false, // Close workflow section
        }));
      } else {
        // If no purchase orders, show workflow first and expanded
        setExpandedSections((prev) => ({
          ...prev,
          "PO Creation": false,
          Workflow: true, // Open workflow section
        }));
      }
    } catch (error) {
      console.error("Error cargando órdenes de compra:", error);
      setErrors((prev) => ({
        ...prev,
        purchaseOrders:
          "Error al cargar las órdenes de compra. Por favor intenta de nuevo.",
      }));
      toast({
        title: "Error",
        description: "Error al cargar las órdenes de compra",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, purchaseOrders: false }));
    }
  }, [clientId, data?.id, toast]);

  // Dynamic section order based on purchase orders
  const getDynamicSections = useCallback(() => {
    if (purchaseOrders && purchaseOrders.length > 0) {
      // If there are purchase orders, show PO Creation first, then Workflow
      return [
        OVERVIEW_SECTIONS.find((s) => s.key === "PO Creation"),
        OVERVIEW_SECTIONS.find((s) => s.key === "Workflow"),
        OVERVIEW_SECTIONS.find((s) => s.key === "Requests"),
        OVERVIEW_SECTIONS.find((s) => s.key === "Suppliers"),
        OVERVIEW_SECTIONS.find((s) => s.key === "Quotations"),
      ].filter(Boolean);
    } else {
      // If no purchase orders, show Workflow first, then PO Creation at the very end
      return [
        OVERVIEW_SECTIONS.find((s) => s.key === "Workflow"),
        OVERVIEW_SECTIONS.find((s) => s.key === "Requests"),
        OVERVIEW_SECTIONS.find((s) => s.key === "Suppliers"),
        OVERVIEW_SECTIONS.find((s) => s.key === "Quotations"),
        OVERVIEW_SECTIONS.find((s) => s.key === "PO Creation"),
      ].filter(Boolean);
    }
  }, [purchaseOrders]);

  // Cargar Suppliers (mantener lógica original)
  const loadSuppliers = useCallback(async () => {
    if (!clientId || !data?.id) return;

    try {
      setIsLoading((prev) => ({ ...prev, suppliers: true }));
      setErrors((prev) => ({ ...prev, suppliers: null }));

      const suppliersData = await getSuppliers(clientId);
      setSuppliers(suppliersData.data);
    } catch (error) {
      console.error("Error cargando proveedores:", error);
      setErrors((prev) => ({
        ...prev,
        suppliers:
          "Error al cargar los proveedores. Por favor intenta de nuevo.",
      }));
      toast({
        title: "Error",
        description: "Error al cargar los proveedores",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, suppliers: false }));
    }
  }, [clientId, data?.id, toast]);

  // Cargar Order Suppliers (mantener lógica original)
  const loadOrderSuppliers = useCallback(() => {
    if (!clientId || !data?.id || isLoadingOrderSuppliersRef.current) return;

    if (sseConnectionRef.current) {
      sseConnectionRef.current.close();
      sseConnectionRef.current = null;
    }

    try {
      isLoadingOrderSuppliersRef.current = true;
      setIsLoading((prev) => ({ ...prev, orderSuppliers: true }));
      setErrors((prev) => ({ ...prev, orderSuppliers: null }));

      const connection = getSuppliersByOrderId(
        clientId,
        data.id,
        (response) => {
          console.log("response", response);
          if (response.success && response.data) {
            const orderSuppliersData = Array.isArray(response.data)
              ? response.data
              : [];
            setOrderSuppliers(orderSuppliersData);
            const supplierIds = orderSuppliersData.map(
              (supplier) => supplier.id
            );
            console.log("orderSuppliersData", orderSuppliersData);
            setSelectedSuppliersIds(supplierIds);
            setIsLoading((prev) => ({ ...prev, orderSuppliers: false }));
            isLoadingOrderSuppliersRef.current = false;
          } else {
            setErrors((prev) => ({
              ...prev,
              orderSuppliers:
                response.message ||
                "Error al cargar los proveedores del pedido",
            }));
            setIsLoading((prev) => ({ ...prev, orderSuppliers: false }));
            isLoadingOrderSuppliersRef.current = false;
          }
        }
      );

      sseConnectionRef.current = connection;
      setSseConnection(connection);
    } catch (error) {
      console.error(
        "Error configurando SSE para proveedores del pedido:",
        error
      );
      setErrors((prev) => ({
        ...prev,
        orderSuppliers:
          "Error al cargar los proveedores del pedido. Por favor intenta de nuevo.",
      }));
      setIsLoading((prev) => ({ ...prev, orderSuppliers: false }));
      isLoadingOrderSuppliersRef.current = false;
    }
  }, [clientId, data?.id]);

  // Cargar datos iniciales
  useEffect(() => {
    if (data?.id) {
      loadOrderItems();
      loadIprProducts();
      loadSuppliers();
      loadPurchaseOrders();
    }
  }, [
    data?.id,
    loadOrderItems,
    loadIprProducts,
    loadSuppliers,
    loadPurchaseOrders,
  ]);

  // Cargar order suppliers cuando se expande Quotations
  const isQuotationsExpanded = expandedSections["Quotations"];
  useEffect(() => {
    if (isQuotationsExpanded && data?.id && clientId) {
      loadOrderSuppliers();
    }

    return () => {
      if (!isQuotationsExpanded && sseConnectionRef.current) {
        sseConnectionRef.current.close();
        sseConnectionRef.current = null;
        setSseConnection(null);
        isLoadingOrderSuppliersRef.current = false;
      }
    };
  }, [isQuotationsExpanded, data?.id, clientId, loadOrderSuppliers]);

  // Limpiar conexión SSE
  useEffect(() => {
    return () => {
      if (sseConnectionRef.current) {
        sseConnectionRef.current.close();
        sseConnectionRef.current = null;
        isLoadingOrderSuppliersRef.current = false;
      }
    };
  }, []);

  // Cargar artículos de cotización cuando se expande
  useEffect(() => {
    const loadQuotationItemsForOrder = async () => {
      console.log("cliente y su id", clientId);
      if (data?.id && clientId && isQuotationsExpanded) {
        await loadQuotationItems(
          clientId,
          data.id,
          setAllQuotationItems,
          (loading) =>
            setIsLoading((prev) => ({ ...prev, allQuotationItems: loading })),
          toast
        );
      }
    };

    loadQuotationItemsForOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.clientId, clientId, isQuotationsExpanded, toast]);

  // Props comunes para todos los componentes
  const commonProps = {
    data,
    currentUser,
    addLog,
    selectedSupplierId,
    onSupplierSelect: setSelectedSupplierId,
  };
  console.log("data", data);
  console.log("clientId", currentUser);
  // Props específicos para cada componente
  const componentProps = {
    Workflow: {
      data, // Pasar datos del registro para el flujo de trabajo
      currentUser:
        currentUser?.email ||
        currentUser?.corporateEmail ||
        currentUser?.personalEmail ||
        "Usuario",
      userApprovalSteps: data?.userApprovalSteps,
      currentUserId: currentUser?._id,
      currentUserEmail:
        currentUser?.email ||
        currentUser?.corporateEmail ||
        currentUser?.personalEmail,
      clientId,
    },
    Requests: {
      ...commonProps,
      orderItems,
      products: iprProducts,
      isLoadingItems: isLoading.orderItems,
      isLoadingProducts: isLoading.iprProducts,
      productsError: errors.iprProducts,
      itemsError: errors.orderItems,
      onRefresh: () => {
        loadOrderItems();
        loadIprProducts();
      },
      suppliers,
      clientId,
      token,
      onItemsUpdated: () => {
        loadOrderItems();
        if (expandedSections["Suppliers"]) {
          loadSuppliers();
        }
      },
    },
    Suppliers: {
      ...commonProps,
      suppliers,
      orderItems,
      selectedSuppliersIds,
      onSuppliersSelect: setSelectedSuppliersIds,
      isLoading: isLoading.suppliers,
      isLoadingItems: isLoading.orderItems,
      error: errors.suppliers,
      onRefresh: loadSuppliers,
      // Añadir props de asignación de proveedores
      clientId,
      onItemsUpdated: () => {
        loadOrderItems();
        if (expandedSections["Suppliers"]) {
          loadSuppliers();
        }
      },
    },
    Quotations: {
      ...commonProps,
      suppliers,
      orderSuppliers,
      orderItems,
      selectedSuppliersIds,
      allQuotationItems,
      isLoadingAllQuotationItems: isLoading.allQuotationItems,
      // Añadir props de gestión de PO
      clientId,
      currentUser,
      mantisBusiness,
    },
    "PO Creation": {
      ...commonProps,
      quotationItems: allQuotationItems,
      isLoadingQuotationItems: isLoading.allQuotationItems,
      quotationItemsError: errors.orderSuppliers,
      purchaseOrders,
      onRefresh: loadOrderSuppliers,
      mantisBusiness,
    },
    "Delivery & Reconciliation": {
      ...commonProps,
      quotationItems: allQuotationItems,
      isLoadingQuotationItems: isLoading.allQuotationItems,
      quotationItemsError: errors.orderSuppliers,
      purchaseOrders,
      onRefresh: loadOrderSuppliers,
    },
  };

  // Log para verificar que los props del WorkflowSection están correctos
  console.log("🔍 OverviewSection - ComponentProps para Workflow:", {
    workflowProps: componentProps["Workflow"],
    hasCurrentUser: !!componentProps["Workflow"].currentUser,
    hasCurrentUserId: !!componentProps["Workflow"].currentUserId,
    hasCurrentUserEmail: !!componentProps["Workflow"].currentUserEmail,
  });

  return (
    <div className="space-y-4">
      {/* Encabezado del documento */}

      {/* Navegación rápida */}
      <QuickNavigation
        sections={getDynamicSections()}
        onNavigate={navigateToSection}
        expandedSections={expandedSections}
      />

      {/* Secciones desplegables */}
      <div className="space-y-4">
        {getDynamicSections().map((section) => {
          const Component = section.component;
          const props = componentProps[section.key];
          const isExpanded = expandedSections[section.key];
          const sectionIsLoading =
            isLoading[section.key.toLowerCase().replace(" ", "")] ||
            (section.key === "Quotations" && isLoading.orderSuppliers);

          return (
            <div key={section.key} id={`section-${section.key}`}>
              <CollapsibleSection
                section={section}
                isExpanded={isExpanded}
                onToggle={() => toggleSection(section.key)}
                isLoading={sectionIsLoading}
              >
                {(isExpanded || section.alwaysOpen) && <Component {...props} />}
              </CollapsibleSection>
            </div>
          );
        })}
      </div>
    </div>
  );
}
